import { useState, useRef, useEffect } from "react";
import ProgressBar from "../ProgressBar";
import LessonUI from "../ScienceLessonUI";
import {generateAdvancedScienceQuiz } from "../AdvanceScienceQuizPool";
import { ScienceStoryModeQuiz } from "../ScienceStoryModeStructure";
import defaultProfile from "../../UserProfile/EmptyProfile.jpg"
import { awardToken } from "../../awardtoken.js";

const PASS_THRESHOLD = 80; // Or whatever percentage you require to pass

export const Grade11ChemistryLessonPages = [
    {
      title: "Introduction to Grade 11 Chemistry",
      content: (
        <>
          <p>
            Welcome to Grade 11 Chemistry! This year, you’ll go beyond the basics and discover how chemistry explains the world around you. From the structure of atoms to the challenges of clean energy and sustainability, every chapter links chemical principles to modern breakthroughs and future careers.
          </p>
          <ul>
            <li>Atomic Theory & Structure – How do we know what atoms are “made of”?</li>
            <li>Chemical Bonding & Molecular Geometry – Why do chemicals stick together, and how do their shapes affect what they do?</li>
            <li>Stoichiometry & Chemical Reactions – How can we predict the amounts of chemicals needed and produced in reactions?</li>
            <li>Solutions, Acids & Bases – What makes something acidic, basic, or neutral? How do solutions impact health and the environment?</li>
            <li>Thermochemistry & Energy – How is energy transferred, transformed, or stored in chemical processes?</li>
            <li>Modern Chemistry in Society – How does chemistry tackle global problems, create new technologies, and shape the future?</li>
          </ul>
          <p>
            <b>Big Question:</b> How will your generation use chemistry to build a safer, cleaner, and more sustainable world?
          </p>
        </>
      ),
    },
    {
      title: "Chapter 1: Atomic Theory & Structure",
      topic: "Atomic Theory & Structure",
      content: (
        <>
          <h4>The Building Blocks of Matter</h4>
          <ul>
            <li>Models of the atom: <b>From Dalton to Quantum Mechanics</b></li>
            <li>Inside atoms: Protons, neutrons, electrons, and what makes elements unique</li>
            <li>Isotopes & ions: How do atoms differ? Why does it matter for nuclear energy and medicine?</li>
            <li>Electron configuration & orbitals: The “address” of every electron, and how this explains periodic trends</li>
            <li>The periodic table: Mapping the elements and predicting their behavior</li>
          </ul>
          <p>
            <b>Explore:</b> Find a news article about a modern particle accelerator. What recent discoveries have changed how we see the atom?
          </p>
          <p>
            <i>Future Lens:</i> Quantum computing and atomic manipulation—imagine designing new materials or medicines atom by atom!
          </p>
        </>
      ),
    },
    {
      title: "Chapter 2: Chemical Bonding & Molecular Geometry",
      topic: "Bonding & Molecular Shapes",
      content: (
        <>
          <h4>How Atoms Connect</h4>
          <ul>
            <li>Ionic, covalent, and metallic bonds: The forces that hold matter together</li>
            <li>Electronegativity, bond polarity, and molecule polarity: What makes water “sticky”?</li>
            <li>Lewis structures & resonance: Drawing molecules and understanding “exceptions” to the octet rule</li>
            <li>VSEPR theory: Predicting and visualizing 3D shapes with models or online tools</li>
            <li>Intermolecular forces: Explaining boiling points, solubility, and why life depends on hydrogen bonding</li>
          </ul>
          <p>
            <b>Activity:</b> Build 3D models (physical kits or online simulations) of common molecules (CO<sub>2</sub>, H<sub>2</sub>O, CH<sub>4</sub>). What shapes do you observe?
          </p>
          <p>
            <i>Futurist View:</i> Imagine materials with programmable shapes—how could molecule design change technology, medicine, or fashion?
          </p>
        </>
      ),
    },
    {
      title: "Chapter 3: Stoichiometry & Chemical Reactions",
      topic: "Stoichiometry & Reactions",
      content: (
        <>
          <h4>Counting Atoms, Predicting Products</h4>
          <ul>
            <li>The law of conservation of mass: No atoms are lost—where do they go?</li>
            <li>Balancing chemical equations: Why is it important in real chemical industries?</li>
            <li>Types of reactions: Identify synthesis, decomposition, displacement, combustion, and redox in everyday examples</li>
            <li>The mole concept & Avogadro’s number: From atoms to grams—why chemists “count” differently</li>
            <li>Stoichiometric calculations: Calculating reactants, products, and limiting reagents (how does this save money in manufacturing?)</li>
            <li>Percent yield: Understanding real-world efficiency—what factors affect yield in industry and research?</li>
          </ul>
          <p>
            <b>Application:</b> Research how green chemistry uses stoichiometry to minimize waste in making everyday products (like soap or biofuel).
          </p>
          <p>
            <i>Challenge:</i> Try a kitchen chemistry experiment—predict the mass of gas produced from mixing baking soda and vinegar, then measure your result!
          </p>
        </>
      ),
    },
    {
      title: "Chapter 4: Solutions, Acids, and Bases",
      topic: "Solutions, Acids & Bases",
      content: (
        <>
          <h4>The Chemistry of Mixing</h4>
          <ul>
            <li>Types of mixtures and solutions; how to distinguish electrolytes from non-electrolytes</li>
            <li>Solubility and saturation: What makes something dissolve, and how do temperature and pressure affect it?</li>
            <li>Calculating concentration: Molarity, percent composition, and how to safely dilute solutions</li>
            <li>Acids and bases: Arrhenius, Brønsted-Lowry, and Lewis definitions—what’s the difference?</li>
            <li>pH scale, indicators, and titration: Measuring acidity in food, environment, and the human body</li>
            <li>Neutralization reactions: Importance in environmental cleanup, medicine (antacids), and agriculture</li>
          </ul>
          <p>
            <b>Try this:</b> Test the pH of local tap water, rain, or soil using indicators (red cabbage works!). How does pH affect local ecosystems?
          </p>
          <p>
            <i>Modern Chemistry:</i> How are scientists creating biodegradable, safe cleaning products with solution and acid-base chemistry?
          </p>
        </>
      ),
    },
    {
      title: "Chapter 5: Thermochemistry & Energy",
      topic: "Thermochemistry & Energy",
      content: (
        <>
          <h4>Energy in Chemical Changes</h4>
          <ul>
            <li>Law of conservation of energy: How do chemical reactions store or release energy?</li>
            <li>Endothermic vs. exothermic reactions: Explore with cold and hot packs or chemical hand warmers</li>
            <li>Calorimetry: How do we measure energy changes? Try a virtual lab!</li>
            <li>Heats of reaction, enthalpy, and Hess’s law: Energy accounting for chemists</li>
            <li>Bond energy: Why does breaking and making bonds absorb or release energy?</li>
            <li>Applications: From combustion engines to fuel cells, photosynthesis, and new battery technologies</li>
          </ul>
          <p>
            <b>Future Focus:</b> Explore how hydrogen fuel cells and solar chemistry are leading the transition to renewable energy. What are the current challenges?
          </p>
          <p>
            <i>Extension:</i> Research a cutting-edge energy material or technology (like perovskite solar cells or solid-state batteries). What is the chemistry behind its function?
          </p>
        </>
      ),
    },
    {
      title: "Chapter 6: Chemistry in Society & Sustainability",
      topic: "Modern Chemistry in Society",
      content: (
        <>
          <h4>Chemistry for a Changing World</h4>
          <ul>
            <li>Green chemistry principles: Minimizing waste, maximizing safety, and protecting the environment</li>
            <li>Plastics, nanomaterials, and advanced materials: How chemistry shapes everything from medical devices to electronics</li>
            <li>Chemistry in medicine: How do chemists design new drugs, vaccines, and rapid diagnostic tests?</li>
            <li>Environmental chemistry: Tackling pollution, improving water quality, and monitoring the air we breathe</li>
            <li>Careers and innovation: Chemists as inventors, researchers, policy advisors, and entrepreneurs</li>
          </ul>
          <p>
            <b>Project:</b> Choose a modern chemical innovation (like biodegradable plastics or water purification technology). Explain the science and discuss its societal impact.
          </p>
          <p>
            <i>Vision:</i> In 2040, how will chemists address climate change, disease, or sustainable agriculture? Share your predictions for the next generation of chemistry breakthroughs.
          </p>
        </>
      ),
    },
    {
      title: "Final Review & Looking Ahead",
      isFinalReview: true,
      content: (
        <>
          <p>
            Chemistry is everywhere—from powering your smartphone to helping farmers grow food and cleaning up pollution. As you finish Grade 11, remember: chemistry is a toolkit for solving tomorrow’s problems.
          </p>
          <ul>
            <li>Review notes, solve sample quizzes, and revisit labs or projects</li>
            <li>Explore real-world breakthroughs (e.g., green energy, new materials, nanotechnology)</li>
            <li>Reflect: How might chemistry shape your future career or help you tackle global challenges?</li>
          </ul>
          <p>
            <b>Next steps:</b> Stay curious—science is always evolving!
          </p>
        </>
      ),
    },
  ];
  

export default function Grade11ChemistryLesson({ onAwardTokens }) {
    /* ─────────── basic UI state ─────────── */
    const [current, setCurrent] = useState(0);
    const [showQuiz, setShowQuiz] = useState(false);
    const [quizResult, setQuizResult] = useState(null);   // {score,total}
    const hasAwardedToken = useRef(false);

    /* ─────────── user & progress persistence ─────────── */
    const [user, setUser] = useState(null);
    const [chaptersPassed, setChaptersPassed] = useState(() =>
      JSON.parse(localStorage.getItem("chem11ChaptersPassed") || "{}")
    );

    useEffect(() => {
      const logged = JSON.parse(localStorage.getItem("loggedInUser"));
      if (logged) setUser(logged);
    }, []);

    /* ─────────── prepare quiz for current topic ─────────── */
    const currentTopic = Grade11ChemistryLessonPages[current]?.topic;
    const quizQuestions = currentTopic
      ? generateAdvancedScienceQuiz({
          subject: "Chemistry",
          grade: 11,
          topic: currentTopic,
          count: 5,
        })
      : [];

    /* ─────────── quiz completion handler ─────────── */
const handleQuizFinish = (score, total) => {
  setShowQuiz(false);
  setQuizResult({ score, total });
  hasAwardedToken.current = false; // reset for next quiz

  const percent = total > 0 ? Math.round((score / total) * 100) : 0;
  if (percent >= PASS_THRESHOLD) {
    // mark chapter as passed
    setChaptersPassed(prev => {
      const updated = { ...prev, [currentTopic]: true };
      localStorage.setItem("chem11ChaptersPassed", JSON.stringify(updated));
      return updated;
    });

    // award token
    if (!hasAwardedToken.current) {
      if (typeof onAwardTokens === "function") onAwardTokens(1);
      else awardToken();
      hasAwardedToken.current = true;
    }

    // --- Save quiz result for Student Dashboard ---
    const quizResults = JSON.parse(localStorage.getItem("quizResults") || "{}");
    quizResults[`chemistry_g11_${currentTopic}`] = {
      score,
      total,
      percent,
      lastCompleted: Date.now(),
    };
    localStorage.setItem("quizResults", JSON.stringify(quizResults));
    // ---------------------------------------------
  }
};

    /* ─────────── gated navigation logic ─────────── */
    const handleNextPage = () => {
      const nextIndex = current + 1;
      const nextPage = Grade11ChemistryLessonPages[nextIndex];

      // Only gate chapters that have a topic!
      if (
        nextPage &&
        nextPage.topic !== currentTopic &&
        currentTopic && // <-- only if current page is a chapter
        !chaptersPassed[currentTopic]
      ) {
        setShowQuiz(true);
        setQuizResult(null);
        return;
      }

      setCurrent(nextIndex);
    };

    /* ─────────── render: quiz mode ─────────── */
    if (showQuiz && quizQuestions.length) {
      return (
        <ScienceStoryModeQuiz
          questions={quizQuestions}
          studentAvatar={user?.avatar ?? defaultProfile}
          onFinish={handleQuizFinish}
        />
      );
    }

    /* ─────────── render: quiz result splash ─────────── */
    if (quizResult) {
      const { score, total } = quizResult;
      const percent = total > 0 ? Math.round((score / total) * 100) : 0;
      const passed = percent >= PASS_THRESHOLD;

      return (
        <div className="quiz-result-container" style={{ textAlign: "center", marginTop: 48 }}>
          <h2 style={{ color: "#1e90ff" }}>Quiz Complete!</h2>
          <p style={{ fontSize: 22, margin: "18px 0" }}>
            Your Score: {score}/{total} ({total > 0 ? percent : "N/A"}%)
          </p>
          {passed ? (
            <div style={{ color: "#228B22", fontSize: 24, margin: "16px 0" }}>
              🎉 Great job—you passed this chapter!
            </div>
          ) : (
            <div style={{ color: "#e67e22", fontSize: 20, margin: "16px 0" }}>
              Score at least {PASS_THRESHOLD}% to unlock the next chapter.
            </div>
          )}
          {passed ? (
            <button
              className="button1"
              style={{ marginTop: 28 }}
              onClick={() => {
                setQuizResult(null);
                setCurrent(i => i + 1); // Go to the next chapter!
              }}
            >
              Next Chapter
            </button>
          ) : (
            <button
              className="button1"
              style={{ marginTop: 28 }}
              onClick={() => setQuizResult(null)}
            >
              Return to Lesson
            </button>
          )}
        </div>
      );
    }

    /* ─────────── render: normal lesson page ─────────── */
    return (
      <div className="lesson-page">
        <ProgressBar
          progress={Math.round((current / (Grade11ChemistryLessonPages.length - 1)) * 100)}
        />

        <LessonUI
          pages={[Grade11ChemistryLessonPages[current]]}
          currentPageIndex={0}
          onPrevPage={current > 0 ? () => setCurrent(i => i - 1) : undefined}
          onNextPage={current < Grade11ChemistryLessonPages.length - 1 ? handleNextPage : undefined}
          backTo="/science"
        />

        {/* Only show the quiz button if a topic exists */}
        {currentTopic && (
          <div style={{ textAlign: "center", marginTop: 24 }}>
            <button
              className="button1"
              onClick={() => {
                setShowQuiz(true);
                setQuizResult(null);
              }}
            >
              Start / Retake Quiz for This Chapter
            </button>
          </div>
        )}
      </div>
    );
}